/**
 * CoverageChainCard — 7 edges of the cross-step coverage chain
 * (Assets → Scenarios → Threats → Attack Paths → Risks → Goals →
 * Controls; plus Requirements → V&V). Each row: edge label, bar at
 * percent linked, percent number. Colour scales: green ≥90, warning
 * ≥70, error < 70.
 *
 * Pure presentation; consumes `data.coverage`.
 */
import React from 'react';
import {
  TypographyH2,
  TypographyMuted,
  TypographySmall,
} from '../../../../components/ui/Typography';
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
} from '../../../../components/ui/Empty';
import type { AggregatedOverviewData, CoverageEdge } from '../../aggregate/types';
import styles from './CoverageChainCard.module.css';

interface CoverageChainCardProps {
  data: AggregatedOverviewData;
}

const colorFor = (pct: number): string => {
  if (pct >= 90) return 'var(--color-status-success)';
  if (pct >= 70) return 'var(--color-status-warning)';
  return 'var(--color-status-error)';
};

export default function CoverageChainCard({ data }: CoverageChainCardProps) {
  const coverage = data.coverage;
  const hasAnyData = coverage.some((c) => c.pct > 0);

  if (!hasAnyData) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <TypographyH2 className={styles.cardTitle}>Traceability Coverage</TypographyH2>
        </div>
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Coverage populates as each step lands records</EmptyTitle>
            <EmptyDescription>First edges appear after Step 3.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <TypographyH2 className={styles.cardTitle}>Traceability Coverage</TypographyH2>
        <TypographyMuted className={styles.cardMeta}>{coverage.length} edge{coverage.length === 1 ? '' : 's'}</TypographyMuted>
      </div>
      <div className={styles.list}>
        {coverage.map((c: CoverageEdge) => (
          <div key={`${c.from}-${c.to}`} className={styles.row}>
            <TypographySmall className={styles.rowLabel}>
              {c.from} <span className={styles.arrow}>→</span> {c.to}
            </TypographySmall>
            <div className={styles.bar}>
              <div
                className={styles.barFill}
                style={{ width: `${c.pct}%`, background: colorFor(c.pct) }}
              />
            </div>
            <TypographySmall className={styles.rowPercent}>{c.pct}%</TypographySmall>
          </div>
        ))}
      </div>
      <TypographyMuted className={styles.help}>
        Lower % means downstream records are missing a link — each gap is an audit finding.
      </TypographyMuted>
    </div>
  );
}
